import { isSignal, Signal } from '@angular/core';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { AppInjector } from 'src/app/app-injector.module';
import { AppContext } from 'src/app/app/context';
import { DataCurrencyDisplay, DataCurrencyLayout, DataCurrencySign, DataDateFormat, DataNumberFormat, DataTimeFormat, LocaleDto, TimestampPrecision } from 'src/app/services/api.services';
import { defaultTimeZone, isNullOrUndefined, TimeZone } from 'src/app/shared/utils';
import { AppCurrencies, AppCurrency } from 'src/app/state/app/app.reducer';
import { DataAggregation, DataDisplayOptions, DataFormat } from '../dataModel';
import { DataType } from '../dataModel/DataType';
import { ERROR, Evaluator } from '../dataQuery/evaluation/Evaluator';
import * as DataQueryExpr from '../dataQuery/expr';
import { logger } from '../utils/logger';
import { IVisualType, VisualType, VisualTypeOptions } from '../visuals/VisualType';

const USE_FOUR_DIGIT_YEAR = true;

export type FormattableNumber =
  number | bigint | BigNumber;

export type FormattableDate =
  Date | moment.Moment |
  DataQueryExpr.DateValue | DataQueryExpr.DateTimeValue;

export type FormattableTime =
  Date | moment.Moment |
  DataQueryExpr.TimeValue | DataQueryExpr.DateTimeValue;

export type FormattableTimestamp = FormattableDate | FormattableTime;

const LOGGER = logger(`DataFormatter`);

export interface DataFormatArgs {
  readonly dataType?: DataType;
  readonly isAggregated?: boolean;
  readonly aggregation?: DataAggregation;
  readonly format?: DataFormat;
}

export interface DisplayFormatArgs {
  readonly dataType?: DataType;
  readonly isAggregated?: boolean;
  readonly aggregation?: DataAggregation;
  readonly display?: DataDisplayOptions;
  readonly ignoreCurrency?: boolean;
  readonly absoluteValue?: boolean;
  readonly includePlusSign?: boolean;
}

export class DataFormatter {
  readonly evaluator: Evaluator;

  constructor(
    public readonly context: DataFormatter.Context,
    evaluatorOrContext: Evaluator | Evaluator.Context | (() => Evaluator.Context))  {
    this.evaluator =
      evaluatorOrContext instanceof Evaluator
        ? evaluatorOrContext
        : new Evaluator(evaluatorOrContext, this.context);
  }


  guessDataType(value: any) {
    if (typeof value === 'boolean')
      return DataType.Boolean;
    else if (typeof value === 'bigint')
      return DataType.Integer;
    else if (typeof value === 'number')
      return DataType.Number;
    else if (value instanceof BigNumber)
      return DataType.Decimal;
    else if (value instanceof DataQueryExpr.DateValue)
      return DataType.DateOnly;
    else if (value instanceof DataQueryExpr.TimeValue)
      return DataType.TimeOnly;
    else if (value instanceof Date || moment.isMoment(value) || value instanceof DataQueryExpr.DateTimeValue)
      return DataType.DateTime;
    else if (typeof value === 'string')
      return DataType.Text;

    return null;
  }


  get defaultFormat(): DataFormat {
    return this.context.defaultFormat;
  }

  getFormatForDataType(dataType: DataType): DataFormat {
    return this.context.formatByDataType.get(dataType) ?? this.defaultFormat;
  }
  
  private getFormat(dataType: DataType, format: DataFormat, aggregation?: DataAggregation) {
    format ??= this.getFormatForDataType(dataType);

    if (aggregation)
      format = aggregation.getResultDataFormat(format);

    return format;
  }

  applyDataFormat(value: any, args?: DataFormatArgs): any {
    if (value === ERROR)
      return value;

    let dataType = args?.dataType;
    let isAggregated = !!args?.isAggregated;
    let aggregation = args?.aggregation;
    let format = args?.format;

    try {
      dataType ??= this.guessDataType(value);
      format = this.getFormat(dataType, format, aggregation);
      args = { ...args, dataType, isAggregated, aggregation, format };

      if (isNullOrUndefined(value)) {
        // treatNullAsBlank applies to text or just nullable values
        if (format.treatNullAsBlank)
          return '';
      }

      if (dataType.isNumeric)
        return this.formatNumeric(value, args);

      if (dataType.isDateTime || dataType.isTimeOnly || dataType.isDateTime)
        return this.formatTimestamp(value, args);

      return value;
    }
    catch (err) {
      LOGGER.error(`Could not format value as ${dataType?.title}`, args, err);
      throw new Error(`Could not format value as ${dataType?.title}`, { cause: err });
    }
  }

  private formatNumeric(value: FormattableNumber, { format }: DataFormatArgs): FormattableNumber {
    if (typeof value === 'number') {
      if (isNaN(value) || !isFinite(value))
        return value;

      if (format.numberRounding)
        value = DataQueryExpr.roundNumericValue(value, format.numberRounding);

      return value;
    }
    else {
      if (!(value instanceof BigNumber))
        value = new BigNumber(value);

      if (value.isNaN() || !value.isFinite())
        return value;

      if (format.numberRounding)
        value = DataQueryExpr.roundBigNumberValue(value, format.numberRounding);

      return value;
    }
  }

  private formatTimestamp(value: FormattableTimestamp, { dataType, format }: DataFormatArgs): FormattableTimestamp {
    if (isNullOrUndefined(format.timestamp) || format.timestamp == TimestampPrecision.Default)
      return value;

    let yr  = true;
    let mth = true;
    let day = true;

    let hasTime = dataType.isTimeOnly || dataType.isDateTime;

    let hr  = hasTime;
    let min = hasTime;
    let sec = hasTime;
    let ms  = hasTime;

    if (format.timestamp == TimestampPrecision.Milliseconds) {
    }
    else if (format.timestamp == TimestampPrecision.Seconds) {
      ms = false;
    }
    else if (format.timestamp == TimestampPrecision.Minutes) {
      ms = sec = false
    }
    else if (format.timestamp == TimestampPrecision.Hours) {
      ms = sec = min = false
    }
    else if (format.timestamp == TimestampPrecision.Day) {
      ms = sec = min = hr = false
    }
    else if (format.timestamp == TimestampPrecision.Month) {
      ms = sec = min = hr = day = false
    }
    else if (format.timestamp == TimestampPrecision.Year) {
      ms = sec = min = hr = day = mth = false
    }

    if (value instanceof DataQueryExpr.DateValue)
      return dateValue(value);
    
    if (value instanceof DataQueryExpr.TimeValue)
      return timeValue(value);
    
    if (value instanceof DataQueryExpr.DateTimeValue) {
      return DataQueryExpr.createDateTimeValue(
        dateValue(value.date),
        timeValue(value.time),
        value.timezone);
    }

    if (moment.isMoment(value)) 
      value = value.toDate();

    let date = new Date(value);
    if (!ms)  date.setMilliseconds(0);
    if (!sec) date.setSeconds(0);
    if (!min) date.setMinutes(0);
    if (!hr)  date.setHours(0);
    if (!day) date.setDate(0);
    if (!mth) date.setMonth(0);
    if (!yr)  date.setFullYear(0);
    value = date;

    return value;

    function dateValue(value: DataQueryExpr.DateValue) {
      return DataQueryExpr.createDateValue(
        yr  ? value.year.value  : 0,
        mth ? value.month.value : 0,
        day ? value.day.value   : 1);
    }

    function timeValue(value: DataQueryExpr.TimeValue) {
      return DataQueryExpr.createTimeValue(
        hr  ? value.hour.value     : 0,
        min ? value.minute.value   : 0,
        sec ? value.second.value   : 0,
        ms  ? value.fraction.value : 0);
    }
  }


  get defaultDisplay(): DataDisplayOptions {
    return this.context.defaultDisplay;
  }

  getDisplayForDataType(dataType: DataType): DataDisplayOptions {
    return this.context.displayByDataType.get(dataType) ?? this.defaultDisplay;
  }

  private getDisplay(dataType: DataType, display: DataDisplayOptions, aggregation?: DataAggregation) {
    display ??= this.getDisplayForDataType(dataType);

    if (aggregation)
      display = aggregation.getResultDataDisplay(display);

    return display;
  }

  formatForDisplay(value: any, args?: DisplayFormatArgs): string {
    let dataType = args?.dataType;
    let isAggregated = !!args?.isAggregated;
    let aggregation = args?.aggregation;
    let display = args?.display;

    try {
      dataType ??= this.guessDataType(value);
      display = this.getDisplay(dataType, display, aggregation);
      args = { ...args, dataType, isAggregated, aggregation, display };

      // If we got display options with formatting expr, that takes precedence
      if (display.formattingQueryExpr) 
        return this.evaluator.evalWithFallback(display.formattingQueryExpr, display.errorQueryExpr);

      // Then, if value is null and we got an expr for that
      if (isNullOrUndefined(value)) {
        let nullQueryExpr = 
          (isAggregated && dataType.isNumeric)
            ? (display.numericAggregationNullQueryExpr ?? display.nullQueryExpr)
            : display.nullQueryExpr;

        if (nullQueryExpr) {
          let formattedValue = this.evaluator.evalWithFallback(nullQueryExpr, display.errorQueryExpr);
          if (!isNullOrUndefined(formattedValue)) {
            let formattedType = this.guessDataType(formattedValue);
            if (formattedType.isText)
              return formattedValue;

            if (formattedType.isAssignableTo(dataType))
              return this.formatForDisplay(formattedValue, args);
          }
        }

        return DataDisplayOptions.defaultStrings.NULL;
      }

      // If the value is erorr sentiel
      if (value === ERROR) {
        if (display.errorQueryExpr)
          return this.evaluator.eval(display.errorQueryExpr);

        return DataDisplayOptions.defaultStrings.ERROR;
      }

      if (dataType.isBoolean)
        return this.getDisplayForBoolean(value, args);

      if (dataType.isNumeric)
        return this.getDisplayForNumeric(value, args);

      if (dataType.isDateOnly || dataType.isTimeOnly || dataType.isDateTime)
        return this.getDisplayForTimestamp(value, args);

      if (dataType.isText)
        return this.getDisplayForText(value, args);
    }
    catch (err) {
      LOGGER.error(`Could not format value for display as ${dataType?.title}`, args, err);
      return DataDisplayOptions.defaultStrings.ERROR;
    }
  }

  private getDisplayForBoolean(value: boolean, { display }: DisplayFormatArgs): string {
    if (value) {
      if (display.trueQueryExpr)
        return this.evaluator.evalWithFallback(display.trueQueryExpr, display.errorQueryExpr);

      return DataDisplayOptions.defaultStrings.TRUE;
    }
    else {
      if (display.falseQueryExpr)
        return this.evaluator.evalWithFallback(display.falseQueryExpr, display.errorQueryExpr);

      return DataDisplayOptions.defaultStrings.FALSE;
    }
  }

  private getDisplayForNumeric(value: FormattableNumber, args: DisplayFormatArgs): string {
    let context = this.context;
    let { dataType, display, absoluteValue } = args;
    let noDecimals = display?.numberRounding?.precision === 0;

    if (typeof value === 'number') {
      if (isNaN(value))
        return nanText();

      if (!isFinite(value)) {
        let negative = !absoluteValue && value < 0;
        return infnityText(negative);
      }

      if (absoluteValue)
        value = Math.abs(value);

      if (display.numberRounding)
        value = DataQueryExpr.roundNumericValue(value, display.numberRounding);

      if (display.significantDigitsOnly) {
        return formatWithSignificiantDigitsOnly(
          BigNumber(value),
          display.numberRounding?.precision ?? 2,
          this.context.locale,
          getBigNumberFormatParts,
          args);
      }

      return formatNumber(value, args);
    }
    else {
      if (!(value instanceof BigNumber))
        value = new BigNumber(value);

      if (value.isNaN())
        return nanText();

      if (!value.isFinite()) {
        let negative = !absoluteValue && value.isNegative();
        return infnityText(negative);
      }
      
      if (absoluteValue)
        value = value.absoluteValue();

      if (display.numberRounding)
        value = DataQueryExpr.roundBigNumberValue(value, display.numberRounding);

      if (display.significantDigitsOnly) {
        return formatWithSignificiantDigitsOnly(
          BigNumber(value),
          display.numberRounding?.precision ?? 2,
          this.context.locale,
          getBigNumberFormatParts,
          args);
      }

      return formatBigNumber(value);
    }

    function nanText() {
      return display.naNText ?? DataDisplayOptions.defaultStrings.NAN;
    }

    function infnityText(negative: boolean) {
      return negative
        ? display.negativeInfinityText ?? DataDisplayOptions.defaultStrings.INF_POSITIVE
        : display.positiveInfinityText ?? DataDisplayOptions.defaultStrings.INF_NEGATIVE;
    }

    function formatNumber(n: number, args?: DisplayFormatArgs) {
      let intl = DataFormatter.getNumberIntl(context, dataType, display, args);
      return intl.format(n);
    }

    function formatBigNumber(n: BigNumber) {
      return getBigNumberFormatParts(n).map(p=>p.value).join('');
    }

    function getBigNumberFormatParts(n: BigNumber) {
      // To format a BigNumber, we split it into integer and fractional parts
      // Then we format each independently, and join them back together
      let [i, f] = n.toFixed().split('.');
      if (isNullOrUndefined(f))
        f = '0';

      let intl = DataFormatter.getNumberIntl(context, dataType, display, args)

      if (noDecimals)
        return intl.formatToParts(BigInt(i));

      return getPartsForNumWithDecimal(intl, i, f);
    }

    function getPartsForNumWithDecimal(intl: Intl.NumberFormat, i: string, f: string) {
      let intParts = intl.formatToParts(i == "-0" ? -0 : BigInt(i))

      let fracParts = intl.formatToParts(Number(`0.${f}`));

      let intEnd = intParts.findIndex(x => x.type === 'decimal') - 1;
      if (intEnd >= 0)
        intParts = intParts.slice(0, intEnd + 1);

      let fracStart = fracParts.findIndex(x => x.type === 'decimal');
      if (fracStart >= 0)
        fracParts = fracParts.slice(fracStart);

      return [
        ...intParts,
        ...fracParts
      ];
  }
  }

  private getDisplayForTimestamp(value: FormattableTimestamp, { dataType, display }: DisplayFormatArgs): string {
    let hasDate = dataType.isDateOnly || dataType.isDateTime;
    let hasTime = dataType.isTimeOnly || dataType.isDateTime;

    let date: Date;

    if (value instanceof DataQueryExpr.DateValue)
      date = dateFromValue(value, null);
    else if (value instanceof DataQueryExpr.TimeValue)
      date = dateFromValue(null, value);
    else if (value instanceof DataQueryExpr.DateTimeValue) {
      date = dateFromValue(
        hasDate ? value.date : null,
        hasTime ? value.time : null);
    }
    else {
      if (moment.isMoment(value))
        date = value.toDate();
      else if (value instanceof Date)
        date = value;
      else if (typeof value === 'string') {
        if (dataType.isTimeOnly) {
          date = DataQueryExpr.createDateTimeValue(
            DataQueryExpr.createDateValue(new Date()),
            DataQueryExpr.createTimeValue(value),
            'utc'
          ).toDate();
        }
        else {
          date = new Date(value);
        }
      }

      date = new Date(Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds()));
    }

    let dateOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };

    let customDate = false;

    switch (display.dateFormat) {
      case DataDateFormat.Long: {
        dateOptions = { dateStyle: 'medium' };
      } break;

      case DataDateFormat.Short: {
        dateOptions = { dateStyle: 'short' };
      }  break;

      case DataDateFormat.DayOfMonth:
        dateOptions = { day: 'numeric' };
        customDate = true;
        break;

      case DataDateFormat.YearPeriod:
        dateOptions = { year: 'numeric', month: '2-digit' };
        customDate = true;
        break;
    }

    let timeOptions: Intl.DateTimeFormatOptions = {};
    switch (display.timeFormat) {
      case DataTimeFormat.NoSeconds:
        timeOptions = {
          timeStyle: 'short',
        };
        break;

      case DataTimeFormat.Full:
        timeOptions = {
          timeStyle: 'medium',
        };
        break;
    }

    if (display.forceTwentyFourClock)
      timeOptions.hourCycle = 'h24';

    dateOptions.timeZone = 'UTC';
    timeOptions.timeZone = 'UTC';

    if (display.timeFormat == DataTimeFormat.NoSeconds)
      date.setSeconds(0);

    if (hasDate && display.dateFormat == DataDateFormat.Short) {
      let formattedValue: string;

      if (hasTime) 
        formattedValue = date.toLocaleString(this.context.locale.id, { ...dateOptions, ...timeOptions });
      else 
        formattedValue = date.toLocaleDateString(this.context.locale.id, dateOptions);

      if (USE_FOUR_DIGIT_YEAR) {
        let year = date.getFullYear();
        let yearString = '' + year;

        if (formattedValue.indexOf(yearString) != -1)
          return formattedValue;

        // HACK: 'short' format for some locales uses 2 digit year
        // There does not appear to be a way to override that, and we don't want to hard code all the formats
        // Instead, format year '1999', then replace the '99' string with the four digit year
        // This works as only the year component could be '99'
        date.setFullYear(1999);

        if (hasTime) {
          if (customDate) {
            formattedValue = `${date.toLocaleDateString(this.context.locale.id, dateOptions)} ${date.toLocaleTimeString(this.context.locale.id, timeOptions)}`;
          }
          else {
            formattedValue = date.toLocaleString(this.context.locale.id, { ...dateOptions, ...timeOptions });
          }
        }
        else {
          formattedValue = date.toLocaleDateString(this.context.locale.id, dateOptions);
        }

        date.setFullYear(year);

        formattedValue = formattedValue
          // Replace middle part
          .replace(/([^0-9])99([^0-9])/g, `$1${yearString}$2`)
          // Replace ending part
          .replace(/([^0-9])99$/g, `$1${yearString}`)
          // Replace starting part
          .replace(/^99([^0-9])/g, `${yearString}$1`);
      }

      return formattedValue;
    }
    else {
      if (hasDate && display.dateFormat == DataDateFormat.YearPeriod) {
        let year = date.toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric' });
        let month = date.toLocaleDateString('en-US', { timeZone: 'UTC', month: '2-digit' });
        return [year, month].join('-');
      }
      else if (hasDate && hasTime) {
        if (customDate) {
          return `${date.toLocaleDateString(this.context.locale.id, dateOptions)} ${date.toLocaleTimeString(this.context.locale.id, timeOptions)}`;
        }
        else {
          return date.toLocaleString(this.context.locale.id, { ...dateOptions, ...timeOptions });
        }
      }
      else if (hasDate) {
        return date.toLocaleDateString(this.context.locale.id, dateOptions);
      }
      else if (hasTime) {
        return date.toLocaleTimeString(this.context.locale.id, timeOptions);
      }
    }
  }

  private getDisplayForText(value: string, args: DisplayFormatArgs): string {
    return value;
  }
}

export class DataCurrencyIndicators {
  constructor(
    readonly code: string,
    readonly sign?: string) { }

  get(type: DataCurrencySign) {
    return (type === DataCurrencySign.Code) ? this.code :
           (type === DataCurrencySign.Sign) ? this.sign : undefined;
  }
}

export namespace DataCurrencyIndicators {
  const currencyIndicatorsCache = new Map<string, DataCurrencyIndicators>();
  let currencyIndicatorsLatest: DataCurrencyIndicators;
  let currencyIndicatorsLatestCacheKey: string;

  export function get(localeId: string, currencyCode: string): DataCurrencyIndicators {
    let cacheKey = `${localeId}.${currencyCode}`.toLowerCase();
    if (cacheKey == currencyIndicatorsLatestCacheKey) {
      if (cacheKey == currencyIndicatorsLatestCacheKey)
        return currencyIndicatorsLatest;
    }

    let indicators = currencyIndicatorsCache.get(cacheKey);
    if (!indicators) {
      let options: Intl.NumberFormatOptions = {
        style: 'currency',
        notation: 'standard',
        currency: getIntlCurrencyCode(currencyCode)
      };

      let intlCode = new Intl.NumberFormat(localeId, { 
        ...options,
        currencyDisplay: 'code'
      });
      
      let intlSign = new Intl.NumberFormat(localeId, {
        ...options,
        currencyDisplay: 'symbol',
      });

      let code = intlCode.formatToParts(0).find(x => x.type === 'currency')?.value;
      let sign = intlSign.formatToParts(0).find(x => x.type === 'currency')?.value;

      indicators = new DataCurrencyIndicators(code, sign);
      currencyIndicatorsCache.set(cacheKey, indicators);
    }

    currencyIndicatorsLatest = indicators;
    currencyIndicatorsLatestCacheKey = cacheKey;

    return indicators;
  }
}


function getIntlCurrencyCode(code: string) {
  if (!code || ['target', '???'].includes(code.toLowerCase()))
    return 'USD';

  try {
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      notation: 'standard',
      currency: code
    });
  }
  catch (err) {
    return 'USD';
  }

  return code;
}

function dateFromValue(d?: DataQueryExpr.DateValue, t?: DataQueryExpr.TimeValue) {
  return new Date(
    Date.UTC(
      d?.year.value ?? 2000,
      d?.month.index ?? 0,
      d?.day.value ?? 1,
      t?.hour.index ?? 0,
      t?.minute.index ?? 0,
      t?.second.index ?? 0));
}

function getNumericGroups(c: number) {
  let list = [];

  let multiplier = BigNumber(1);
  let half = BigNumber(0);

  for (let i = 0; i < c; ++i) {
    list.push({
      group: i,
      multiplier,
      half
    });

    multiplier = multiplier.multipliedBy(1000);
    half = multiplier.dividedBy(2);
  }

  return list;
}

let numericGroups = getNumericGroups(7);

const localeNumericGroupsCache = new Map<string, any>();

function formatWithSignificiantDigitsOnly(
  m: BigNumber,
  decimalPlaces: number, 
  locale: LocaleDto,
  formatPartsGetter: (m: BigNumber) => Intl.NumberFormatPart[],
  args?: any): string {
  let groups = localeNumericGroupsCache.get(locale.id);
  if (!groups) {
    groups = Object.entries(locale.formats.numeric.groupSymbols)
      .map(x => ({
        group: parseInt(x[0]),
        magnitude: x[1]
      }))
      .filter(x => x.group >= 0 && x.group < numericGroups.length)
      .map(x => ({ ...x, ...numericGroups[x.group] }))
      .sort((a, b) => a.group - b.group);

    localeNumericGroupsCache.set(locale.id, groups);
  }
    
  let magnitude = '';

  let c = countGroups(m);
  let g = groups.find(x => x.group == c);
  if (g) {
    magnitude = g.magnitude;
    m = m.dividedBy(g.multiplier);
  }
        
  let parts = formatPartsGetter(m).map((part, index) => ({index, ...part}));

  //Ensure the magnitude sign is always placed after the last numerical part
  let lastNumberIndex = Math.max(...parts.filter(x => x.type === 'integer' || x.type === 'fraction' || x.type === 'decimal').map(x => x.index)) + 1;

  let numberAndBefore = parts.slice(0, lastNumberIndex);
  let afterNumber = lastNumberIndex < parts.length ? parts.slice(lastNumberIndex) : [];

  return [...numberAndBefore, { value: `${magnitude} ` }, ...afterNumber].map(i=>i.value).join('').trim();


  function countGroups(m: BigNumber)
  {
      let value = m.abs();

      for (let i = groups.length - 1; i >= 0; --i) {
        let g = groups[i];

        if (value.gte(g.half)) {
          return i;
        }
      }

      return 0;
  }
}

export namespace DataFormatter {
  export function createDefault(context: Context): DataFormatter {
    let appContext = AppInjector.get(AppContext);
    let user = appContext.user();
    let currencies = context.currencies;

    context = {
      ...context,
      
      defaultFormat: context.defaultFormat ?? {},
      formatByDataType: context.formatByDataType ?? new Map<DataType, DataFormat>(),

      defaultDisplay: context.defaultDisplay ?? {},
      displayByDataType: context.displayByDataType ?? new Map<DataType, DataDisplayOptions>(),

      locale: context.locale ?? appContext.locale(),
      tz: context.tz ?? appContext.timeZone(),
      currencies,
      currency: context.currency ?? currencies.baseCurrency
    };
    
    return new DataFormatter(<Context>context, () => {
      let timestamp = DataQueryExpr.createDateTimeValue(moment(), context.tz?.id ?? defaultTimeZone.id);
      let timestampUtc = DataQueryExpr.createDateTimeValue(moment.utc(), 'utc');

      return {
        locale: context.locale,
        currency: context.currency,

        organisation: user.organisation,

        timestamp,
        timestampUtc,
        tz: context.tz,

        user: {
          name: user.username,
          role: user.role,
          isImpersonating: user.isImpersonating,

          timestamp,
          timestampUtc,
          tz: context.tz,
        },
        
        visualData: 
          context.visualData
            ? isSignal(context.visualData) 
              ? context.visualData()
              : context.visualData
            : null,

        parameterResolver: (name: string) => {
          return null;
        }
      };
    });
  }

  export interface VisualData {
    readonly visualId?: number;
    readonly name?: string;
    readonly type?: IVisualType;
    readonly typeOptions?: VisualTypeOptions;
  }

  export interface Context {
    readonly defaultFormat?: DataFormat;
    readonly formatByDataType?: Map<DataType, DataFormat>;

    readonly defaultDisplay?: DataDisplayOptions;
    readonly displayByDataType?: Map<DataType, DataDisplayOptions>;

    readonly locale?: LocaleDto;
    readonly tz?: TimeZone;
    readonly currencies: AppCurrencies;
    readonly currency?: AppCurrency;

    readonly visualData?: VisualData | Signal<VisualData>;
  }

  export function getNumberIntl(
    context: Context, 
    dataType: DataType, 
    display: DataDisplayOptions,
    args?: DisplayFormatArgs) {
    let decimalPlaces = display.numberRounding?.precision ?? (dataType.isInteger ? 0 : undefined);
    let ignoreCurrency = !!args?.ignoreCurrency;

    let options: Intl.NumberFormatOptions = {
      notation: 'standard',

      useGrouping: !!display.showThousandSeparator,

      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    };

    if (args?.includePlusSign) {
      options.signDisplay = 'exceptZero';
    }

    if (!ignoreCurrency && display.numberFormat === DataNumberFormat.Currency) {
      options.currency = getIntlCurrencyCode(context.currencies.getCurrencyCode(context.currency?.currencyCode));

      options.currencyDisplay =
        (display.currency?.layout === DataCurrencyLayout.Default)
          ? (display.currency.sign === DataCurrencySign.Code ? 'code' 
           : display.currency.sign === DataCurrencySign.Sign ? 'symbol' : undefined)
          : undefined;
      
      options.style = 'currency';
    }
    else {
      switch (display.numberFormat) {
        case DataNumberFormat.Scientific:
          options.notation = 'scientific';
          break;

        case DataNumberFormat.Percentage:
          options.style = 'percent';
          break;

        default:
          options.style = 'decimal';
          break;
      }
    }

    return new Intl.NumberFormat(context.locale.id, options);
  }

  export function isNegative(value: any) {
    if (typeof value === 'number') {
      return value < 0;
    }

    if (typeof value === 'bigint') {
      return value < 0;
    }

    if (!(value instanceof BigNumber) && typeof value !== 'string')
      return false;

    let n = BigNumber(value);
    if (n.isNaN())
      return false;

    return n.isNegative();
  }
}
